import type { IconHandle } from "@animateicons/react";
import type { LucideProps } from "lucide-react";
import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
  useEffect,
  useRef
} from "react";
import type { AnimatedGlyph } from "./registry";

/**
 * Renders one `@animateicons/react` glyph and drives it from the interactive
 * control that owns it, so hovering anywhere in an `IconButton` — padding and
 * ripple area included — plays the icon, not just the glyph's own box.
 *
 * The glyph animates itself on hover *unless* a ref is attached, in which case
 * it hands its pointer callbacks back and waits to be driven through
 * `startAnimation` / `stopAnimation`. We attach one, so both halves below are
 * ours — but only ever one of them at a time. Exactly one driver may be live:
 * `mouseenter`/`mouseleave` fire on an element's own box and, unlike
 * `mouseover`/`mouseout`, say nothing about ancestors, so a glyph listening
 * alongside its control reports a *leave* every time the pointer drifts from
 * the glyph back onto the control's padding — stopping an animation the
 * control is still hovering, and flickering on any jitter. The control wins
 * when there is one; the glyph's handlers stand down and cover the standalone
 * icon alone.
 *
 * The ancestor half reads the DOM because there is nothing else to read. Hover
 * and focus live on the control's own node and neither React nor MUI publishes
 * them downward — React events travel up, so a descendant cannot observe an
 * ancestor's, and no MUI button exposes its hover state as context. The
 * declarative alternative is a prop threaded through every call site, which is
 * the several-hundred consumers this abstraction exists to keep untouched. The
 * lookup is confined to this file: `Icon` and its callers never see it.
 */

const INTERACTIVE_ANCESTOR = [
  "button",
  "a[href]",
  '[role="button"]',
  '[role="menuitem"]',
  '[role="tab"]',
  "label",
  "summary"
].join(",");

// jsdom's selector engine does not know `:focus-visible`. A focus it cannot
// classify counts as keyboard-driven rather than silently losing the animation.
const isKeyboardFocus = (element: Element): boolean => {
  try {
    return element.matches(":focus-visible");
  } catch {
    return true;
  }
};

const assignRef = <T,>(ref: ForwardedRef<T>, value: T | null): void => {
  if (typeof ref === "function") ref(value);
  else if (ref) ref.current = value;
};

/**
 * `Icon` forwards an svg-flavoured prop bag, and the glyph renders a div, so
 * the two disagree on the element type inside every event handler and nowhere
 * else. The host takes `Icon`'s shape — it is the only caller — and absorbs the
 * mismatch in one cast below rather than leaking it upward.
 */
export interface AnimatedGlyphHostProps extends Omit<
  LucideProps,
  "color" | "fill" | "ref" | "size"
> {
  glyph: AnimatedGlyph;
  color: string;
  size: number;
}

const AnimatedGlyphHost = forwardRef<SVGSVGElement, AnimatedGlyphHostProps>(
  ({ glyph: Glyph, style, ...rest }, ref) => {
    const hostRef = useRef<HTMLSpanElement>(null);
    const handleRef = useRef<IconHandle>(null);
    // Set by the effect below, read by the glyph's own handlers at event time —
    // which is always after the effect has run, so the two never both drive.
    const controlRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
      const host = hostRef.current;
      if (!host) return;

      // The glyph's own ref is the animation handle, not a node, so `Icon`'s
      // SVGSVGElement ref is bridged from the rendered svg instead.
      assignRef(ref, host.querySelector("svg"));

      const control = host.closest<HTMLElement>(INTERACTIVE_ANCESTOR);
      controlRef.current = control;
      if (!control) return () => assignRef(ref, null);

      const start = () => handleRef.current?.startAnimation();
      const stop = () => handleRef.current?.stopAnimation();
      const onFocusIn = (event: FocusEvent) => {
        if (isKeyboardFocus(event.target as Element)) start();
      };

      control.addEventListener("mouseenter", start);
      control.addEventListener("mouseleave", stop);
      control.addEventListener("focusin", onFocusIn);
      control.addEventListener("focusout", stop);

      return () => {
        assignRef(ref, null);
        controlRef.current = null;
        control.removeEventListener("mouseenter", start);
        control.removeEventListener("mouseleave", stop);
        control.removeEventListener("focusin", onFocusIn);
        control.removeEventListener("focusout", stop);
      };
    }, [ref]);

    // The standalone case only: crossing the glyph's own edge inside a control
    // is not a hover change for that control, and acting on it would cut the
    // animation short. See the note above the file.
    const onGlyphEnter = () => {
      if (!controlRef.current) handleRef.current?.startAnimation();
    };
    const onGlyphLeave = () => {
      if (!controlRef.current) handleRef.current?.stopAnimation();
    };

    return (
      // `display: contents` keeps the lookup anchor out of layout, so an
      // animated icon measures and aligns exactly like the static one it
      // replaces. The glyph's own wrapper carries the a11y and data attributes.
      <span ref={hostRef} style={{ display: "contents" }}>
        <Glyph
          ref={handleRef}
          onMouseEnter={onGlyphEnter}
          onMouseLeave={onGlyphLeave}
          // The package styles its wrapper with Tailwind classes this app does
          // not ship, so the inline-flex it assumes is restated here.
          style={{ alignItems: "center", display: "inline-flex", ...style }}
          {...(rest as unknown as ComponentPropsWithoutRef<AnimatedGlyph>)}
        />
      </span>
    );
  }
);

AnimatedGlyphHost.displayName = "AnimatedGlyphHost";

export default AnimatedGlyphHost;
